import { createContext, createElement, useContext, type ReactNode } from 'react';
import { Blank } from './Blank.tsx';
import { ReadingNote } from './Reading.tsx';
import { useSupply } from './sources.ts';

/* ==========================================================================
   Is a value on its way, or is this it?

   Everything a shape in this module draws hangs off that one question, and
   after four rounds the answer is that IT CHANGES ONE SENTENCE AND NOTHING
   ELSE.

     **THERE IS ONE DRAWING OF A PANEL WITH NO READINGS IN IT.**

   Not three. The console used to have a loading picture (a grid of grey
   pills), a failure picture and an empty picture, and an operator had to learn
   all three to read one panel. They are the same fact — this panel is not
   showing you data — and the only thing that legitimately differs between them
   is the words on one line and whether the 1px bar under the panel header is
   running.

   So every shape in Skeleton.tsx draws exactly this, in every state:

     · CHROME THE PANEL KNOWS WITHOUT READING ANYTHING — a table's column
       headings, a rail's field names, a plot's lane labels, thresholds and
       axis, a matrix's metric lanes. Named by the caller, drawn for real, with
       `—` where a single value would have gone (THE BAR §3: a missing value is
       an em dash, never a zero and never a blank).
     · ONE LINE of --text-mid where the first row would be (THE BAR §6).
     · Hatched ground under it, and nothing that could be mistaken for a row.

   A grey pill and a ruled grid of 26 empty bordered cells are the same promise
   in two inks — both say "rows are coming" at a glance, both are 700px tall on
   /fleet/list, and one of them is a lie in exactly the case where an operator
   most needs the truth. Neither is drawn any more. THE BAR §6 is explicit:
   "Loading: the panel keeps its header and shows a 1px indeterminate bar under
   it... never a skeleton shimmer over a whole page."

     reading   a request is genuinely in flight. Same drawing; the line reads
               `Reading…`; `aria-busy`; the panel's 1px bar is running.
     stopped   the read failed. Same drawing; the line reads `Nothing has been
               read.`; the panel's fault line sits above it.
     empty     the read landed and carried nothing. Same drawing; the line is
               the caller's absence sentence.
     drawn     the readings are on screen over this same chrome.

   `stopped` and `empty` differ in their words and their colour and in nothing
   else, which is why `useStanding` collapses them into one answer for the
   shapes: both mean NOTHING IS COMING, and a shape that handles one correctly
   handles the other correctly by construction. The distinction stays available
   as `reason` for the two places it changes a pixel.

   ---------------------------------------------------------------------------
   WHY THE HOOK CHECKS THE CACHE AND NOT ONLY ITS CALLER
   ---------------------------------------------------------------------------
   The mode arrives by context because the caller writes `skeleton={<Skeleton…/>}`
   once and must not thread a flag through it. But the caller can be WRONG, and
   is wrong in one specific, invisible way: a panel that computes its own state
   from TanStack's `isError` reports "still reading" for ever against a dead
   API, because query-core clears `error` and drops `status` back to `pending`
   at the start of every refetch of a query that has never held data. Twenty
   seconds into a total outage the /fleet panels were still drawing bright
   blocks and announcing "Reading the fleet matrix" to a screen reader.

   So the promise is checked before it is kept: if the surface is starved — a
   transport failure with not one mounted source answering — no block is drawn
   anywhere on it, whatever the panel believes, and the shape prints the one
   line instead. See `useSupply`.
   ========================================================================== */

export type Standing = 'reading' | 'stopped' | 'empty' | 'drawn';

export interface StandingValue {
  mode: Standing;
  /**
   * The one line, drawn at the first row's position.
   *
   * Omitted on a `stopped` and the shape writes its own — because a panel that
   * has stopped and says nothing about it is the 600px of silent void this
   * module exists to remove. `null` silences it, and nothing in the console
   * passes `null` any more: a panel with a fault line above it still owes the
   * operator a line where the first row would be, because the fault line says
   * WHY and the row says WHAT IS THERE, and an empty tbody under a red bar is
   * the one shape a degraded screen must never take.
   */
  note?: ReactNode;
}

/** What a shape may draw, after the promise has been checked. */
export interface Reading {
  /**
   * `reading` — a request is in flight. The drawing is identical to `empty`;
   *             what differs is the line's words and `aria-busy`.
   * `empty`   — nothing is coming: known chrome, em dashes, and `note` at the
   *             first row's position. Covers the failed read and the empty one.
   * `drawn`   — the data is on screen.
   */
  mode: 'reading' | 'empty' | 'drawn';
  /** Which of the four it really is, for the two cases that differ. */
  reason: Standing;
  /** The one line, at the first row's position. Present in all three. */
  note?: ReactNode | undefined;
}

export const StoppedContext = createContext<StandingValue>({ mode: 'reading' });

/* ==========================================================================
   WHO OWNS THE ONE MOVING THING.

   THE BAR §6 asks a loading panel for "a 1px indeterminate bar under [the
   header]", and §7 permits exactly one of them per panel — it is the only
   continuous motion in the console other than the playhead, and it is what
   separates "working on it" from "given up" without spending a colour on it.

   Two components in the tree can draw it and they must never both draw it:

     · `Panel`'s own `loading` prop, which `ReadPanel` passes off the read.
       That bar sits in the header's own bottom edge, which is the ideal
       position — but it is only reachable by a caller that uses `ReadPanel`.
     · the instrument itself (Skeleton.tsx), at the top edge of the panel
       BODY, which is one hairline lower and is reachable by every caller,
       including the five surfaces that pair a bare `Panel` with a skeleton
       and therefore ran no bar at all.

   So the shapes draw it by default, and `ReadPanel` — the one component that
   knows the header above it is already running one — turns them off through
   this context. A route that mixes the two gets one bar per panel either way,
   which is the property that matters: a 2px double rule under one panel's
   header and a single under the next is how an operator learns to stop
   reading the chrome.
   ========================================================================== */
export const HeaderBar = createContext(false);

/**
 * The line a shape prints when nothing has arrived and the caller did not
 * write a sentence for it.
 *
 * It states THIS PANEL'S fact and not the console's: the cause is already on
 * screen once, in the banner at the top of the surface (`LINK DOWN · Nothing
 * is reaching the API.`), and four panels restating it is one event described
 * five times to an operator scanning for the one that differs. What the panel
 * alone can say is that it holds nothing and that nothing is a failure rather
 * than an absence — `No groups.` would be a claim about the fleet that nobody
 * has read. The state word beside it is the same one the fault line, the read
 * tag and the supply ledger print, so the screen has one vocabulary.
 *
 * It carries no cause. The cause is on screen twice already — once in the
 * banner at the top of the surface (`LINK DOWN · Nothing is reaching the API`)
 * and once in the panel's own fault line, with the endpoint, the status and
 * the run count on it. A third copy of `no reply` inside the same panel, 20px
 * below the second, is one event described three times to an operator scanning
 * eight panels for the one that differs.
 */
const NOT_READ: ReactNode = createElement(Blank, null, 'Nothing has been read.');

/* THE LINE WHILE A REQUEST IS ACTUALLY IN FLIGHT.

   One word, in the same ink, at the same position, at the same row height as
   the two lines above. It replaces three things that used to occupy this slot
   and disagreed with each other: a grid of grey pills, a `•••` in the KPI
   band, and three hand-written sentences ("Reading roster…", "Reading capture
   sessions…", "Reading command ledger…") sitting under three different panel
   headers. A panel that is reading, a panel that failed and a panel that came
   back empty are now one picture with one sentence swapped, which is the only
   version of this an operator can read at a glance across eight panels.

   The motion that says work is in flight is the 1px indeterminate bar under
   the panel header, and it is the only motion there is (THE BAR §7).

   IT CARRIES ITS AGE. `Reading…` on its own is the one claim on a degraded
   surface that cannot be checked: it reads identically at 300 ms and at four
   minutes, so a hung API produces a console that says it is working on it for
   the rest of the shift. `ReadingNote` appends the duration, on the same 1 Hz
   beat and in the same mono as every other age on the screen. */
const READING: ReactNode = createElement(Blank, null, createElement(ReadingNote));

export function useStanding(): Reading {
  const { mode, note } = useContext(StoppedContext);
  const { starved, opening, faults } = useSupply();

  if (mode === 'drawn') return { mode: 'drawn', reason: 'drawn' };
  if (mode === 'reading') {
    /* A caller that holds the read passes its own line, so the duration on it
       is THIS panel's wait rather than the surface's oldest. Everything else
       — the five surfaces that pair a bare `Panel` with a skeleton and mount
       no provider at all — gets the default, which measures the same thing
       one rung less precisely. */
    return starved
      ? { mode: 'empty', reason: 'stopped', note: NOT_READ }
      : { mode: 'reading', reason: 'reading', note: note ?? READING };
  }
  /* THE OTHER HALF OF "THE CALLER CAN BE WRONG", AND IT IS WRONG THE OTHER WAY.
     ---------------------------------------------------------------------
     The check above stops a panel drawing blocks against a dead API. This one
     stops the opposite, which is just as wrong and much easier to ship: a
     surface whose FALLBACK LAYOUT — the one it renders before its subject has
     arrived — hard-codes `mode: 'stopped'`, because at the time it was written
     "no robot yet" and "no robot ever" looked like the same branch. Two
     seconds into a normal page load on a perfectly healthy API, /fleet/detail
     rendered `Nothing has been read.` in four panels while four requests were
     in flight, and a screen reader was told the same.

     So: a `stopped` claimed while a first read is genuinely in flight AND NOT
     ONE mounted source carries a committed fault is a claim about the future,
     and the module does not make it. The instant anything actually fails —
     any class, including a 404 on the robot the page is for — the caller's
     word stands and the panel says so. */
  if (mode === 'stopped' && opening && faults === 0) {
    return { mode: 'reading', reason: 'reading', note: READING };
  }
  /* A stopped panel always says so, in its own body, whether or not the caller
     thought to write the sentence — five panels each stating their own state is
     not repetition, it is the property that lets an operator read ONE panel and
     know whether to trust it. `null` is the deliberate silence. */
  if (mode === 'stopped' && note === undefined) {
    return { mode: 'empty', reason: 'stopped', note: NOT_READ };
  }
  return { mode: 'empty', reason: mode, note };
}

/** True whenever nothing is on its way — the failed read and the empty one. */
export function useStopped(): boolean {
  return useStanding().mode !== 'reading';
}
